// hooks/useApiAction2.ts
import { useState, useCallback } from 'react';

type ApiActionType = 'create' | 'update' | 'remove' | 'default';

interface ConfirmConfig {
    title?: string;
    message: string;
}

interface UseApiActionOptions<T> {
    setData?: React.Dispatch<React.SetStateAction<T[]>>;
    pk?: keyof T | (keyof T)[];
    defaultConfirmFn?: (message: string, title?: string) => Promise<boolean>;
}

export function useApiAction<T extends Record<string, any>>({
    setData,
    pk = 'id',
    defaultConfirmFn,
}: UseApiActionOptions<T> = {}) {
    const [activeType, setActiveType] = useState<ApiActionType | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Нормализуем pk в массив для поддержки составных ключей
    const pkList = Array.isArray(pk) ? pk : [pk];

    // Функция проверки равенства элементов с учетом составного PK
    const isSameItem = (item: T, target: T | any) => {
        return pkList.every((key) => item[key] === (target[key] !== undefined ? target[key] : target));
    };

    const execute = useCallback(async (
        action: () => Promise<any>,
        type: ApiActionType,
        confirmConfig?: ConfirmConfig,
        customErrorMessage?: string
    ) => {
        setErrorMessage(null);

        if (confirmConfig && defaultConfirmFn) {
            const isConfirmed = await defaultConfirmFn(confirmConfig.message, confirmConfig.title);
            if (!isConfirmed) return false;
        }

        setActiveType(type);
        try {
            const result = await action();
            return result;
        } catch (err) {
            console.error(`API Action (${type}) error:`, err);
            const msg = customErrorMessage || (err instanceof Error ? err.message : 'Unknown error');
            setErrorMessage(msg);
            throw err;
        } finally {
            setActiveType(null);
        }
    }, [defaultConfirmFn]);

    return {
        // Умный upsert (создание или обновление)
        upsert: async ({
            data,
            action,
            createAction,
            updateAction,
            onSuccess,
            errorMessage,
        }: {
            data: T;
            action?: (data: T) => Promise<T>; // Если метод один (например, Save)
            createAction?: (data: T) => Promise<T>; // Либо раздельные
            updateAction?: (data: T) => Promise<T>;
            onSuccess?: (savedItem: T) => void;
            errorMessage?: string;
        }) => {
            // Определяем режим (единый PK или составной проверяем по наличию значений)
            const isEdit = pkList.every((key) => data[key] !== undefined && data[key] !== null && data[key] !== '');
            
            let targetAction: () => Promise<T>;
            if (action) {
                targetAction = () => action(data);
            } else {
                targetAction = isEdit 
                    ? () => updateAction!(data) 
                    : () => createAction!(data);
            }

            const savedItem = await execute(
                targetAction, 
                isEdit ? 'update' : 'create', 
                undefined, 
                errorMessage
            );

            if (savedItem && setData) {
                setData((prev) => 
                    isEdit
                        ? prev.map((item) => isSameItem(item, savedItem) ? savedItem : item)
                        : [...prev, savedItem]
                );
            }

            if (savedItem) {
                onSuccess?.(savedItem);
            }
            return savedItem;
        },

        // Умный remove (удаление)
        remove: async ({
            id, // может быть примитивом (number/string) или объектом с составным ключом
            action,
            confirm,
            onSuccess,
            errorMessage,
        }: {
            id: any;
            action: (id: any) => Promise<any>;
            confirm?: ConfirmConfig;
            onSuccess?: () => void;
            errorMessage?: string;
        }) => {
            const success = await execute(
                () => action(id),
                'remove',
                confirm,
                errorMessage
            );

            // Если запрос прошел успешно и передан setData — фильтруем стейт
            if (success !== false && setData) {
                setData((prev) => prev.filter((item) => !isSameItem(item, id)));
            }

            if (success !== false) {
                onSuccess?.();
            }
            return success;
        },

        isActing: activeType !== null,
        errorMessage,
    };
}

/*

// Example

// Инициализируем (передаем сеттер и первичный ключ, можно составной: ['orgId', 'id'])
const { upsert, remove, isActing, errorMessage } = useApiAction({
    setData,
    pk: 'id', 
    defaultConfirmFn: showConfirm,
});

// Сохранение (создание / обновление)
const handleUpdate = async (formData: WireguardConf) => {
    await upsert({
        data: formData,
        updateAction: api.putWireguardConf,
        createAction: api.postWireguardConf,
        onSuccess: handleCloseModal,
        errorMessage: t('VpnList.save_error'),
    });
};

// Удаление
const handleDelete = async (id: number) => {
    await remove({
        id,
        action: api.deleteWireguardConf,
        confirm: {
            title: t('common.delete_confirmation'),
            message: `${t('VpnList.Are_you_sure_you_want_to_delete_vpn')} ${id}?`,
        },
        errorMessage: t('VpnList.delete_error'),
    });
};

*/